import React, { useEffect, useState, useContext } from 'react';
import { Table, Button, Modal, Input, message, Avatar, Space, Select, TreeSelect, Upload, Tag, Tooltip, Popconfirm } from "antd";
import { EditOutlined, UserOutlined, ShopOutlined, UploadOutlined, DownloadOutlined, FileTextOutlined, MailOutlined, HomeOutlined, DeleteOutlined } from "@ant-design/icons";
import DualProfileForm from "./DualProfileForm";
import { GetAlumnesMetadata } from "../../../dao/metadata/GetAlumnesMetadata";
import { GetEmpresas } from '../../../dao/metadata/GetEmpresas';
import { PostAssignacio } from '../../../dao/metadata/ManageAssignacions';
import { GetUserAules } from '../../../dao/abp/GetUserAules';
import { AuthContext } from '../../../config/AuthContext';

const { Option } = Select;

const Alumnos = () => {
    const { userId } = useContext(AuthContext);
    const [alumnes, setAlumnes] = useState<any[]>([]);
    const [aulas, setAulas] = useState<any[]>([]);
    const [selectedAula, setSelectedAula] = useState<string>("0");
    const [loading, setLoading] = useState(false);
    const [uploadingId, setUploadingId] = useState<number | null>(null);

    const [filters, setFilters] = useState({ nom: '', idCurs: 0, poblacio: '' });
    const [cursosTree, setCursosTree] = useState<any[]>([]);

    const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
    const [selectedAlumneForAssign, setSelectedAlumneForAssign] = useState<any>(null);
    const [companiesList, setCompaniesList] = useState<any[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);

    const fetchAulas = async () => {
        if (!userId) return;
        const data = await GetUserAules(userId.toString());
        if (data) {
        const dualAules = data.filter((a: any) => Number(a.is_dual) === 1);
            setAulas(dualAules);
        }
    };

    const fetchCursos = async () => {
        const { GetCursos } = await import('../../../dao/metadata/ManageCursos');
        const data = await GetCursos();
        const roots: any[] = [];
        const map: any = {};
        data.forEach((item: any) => {
            map[item.id] = { ...item, key: item.id, value: item.id, title: item.label, children: [] };
        });
        data.forEach((item: any) => {
            if (item.parent_id && map[item.parent_id]) {
                map[item.parent_id].children.push(map[item.id]);
            } else if (!item.parent_id) {
                roots.push(map[item.id]);
            }
        });
        setCursosTree(roots);
    };

    const fetchAlumnes = async () => {
        if (!userId) return;
        setLoading(true);
        const cursId = filters.idCurs === 0 ? undefined : filters.idCurs;
        const data = await GetAlumnesMetadata(selectedAula, filters.nom, cursId, filters.poblacio, userId);
        setAlumnes(data || []);
        setLoading(false);
    };

    useEffect(() => {
        fetchAulas();
        fetchCursos();
    }, [userId]);

    useEffect(() => {
        fetchAlumnes();
    }, [selectedAula, filters, userId]);

    const handleOpenAssign = async (record: any) => {
        setSelectedAlumneForAssign(record);
        setIsAssignModalVisible(true);
        const companies = await GetEmpresas(undefined, 0, undefined);
        setCompaniesList(companies || []);
    };

    const handleAssignCompany = async () => {
        if (!selectedAlumneForAssign || !selectedCompanyId) return;
        const success = await PostAssignacio({ 
            id_alumne: selectedAlumneForAssign.id_usuari, 
            id_empresa: parseInt(selectedCompanyId), 
            estat: 1 
        });
        if (success) {
            message.success('Empresa assignada correctament');
            setIsAssignModalVisible(false);
            fetchAlumnes();
        }
    };

    const handleFileUpload = async (idUsuari: number, file: File) => {
        setUploadingId(idUsuari);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost').replace(/\/$/, "");
            const response = await fetch(`${baseUrl}:8061/metadata/alumnes/${idUsuari}/documents/CV`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });
            if (response.ok) {
                message.success('CV pujat correctament');
                fetchAlumnes();
            } else {
                message.error('Error al pujar el CV');
            }
        } catch (e) {
            message.error('Error de connexió al pujar el CV');
        }
        setUploadingId(null);
        return false;
    };

    const handleDeleteCV = async (idUsuari: number) => {
        try {
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost').replace(/\/$/, "");
            const res = await fetch(`${baseUrl}:8061/metadata/alumnes/${idUsuari}/documents/CV`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (res.ok) {
                message.success('CV esborrat correctament');
                fetchAlumnes();
            } else {
                message.error('Error a l\'esborrar el CV');
            }
        } catch (e) {
            message.error('Error a l\'esborrar el CV');
        }
    };

    const handleDownload = async (idUsuari: number, nom: string, cognoms: string, type: 'CV' | 'NUSS' = 'CV') => {
        if (!idUsuari) {
            message.warning("No s'ha pogut identificar l'usuari per a la descàrrega.");
            return;
        }
        try {
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost').replace(/\/$/, "");
            const res = await fetch(`${baseUrl}:8061/metadata/alumnes/${idUsuari}/documents/${type}/download`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });

            if (!res.ok) {
                message.error('El document no existeix o no es pot descarregar.');
                return;
            }

            const contentDisposition = res.headers.get('Content-Disposition');
            let filename = `${type}_${nom}_${cognoms}.pdf`.replace(/\s+/g, '_');
            if (contentDisposition && contentDisposition.includes('filename=')) {
                let originalExt = contentDisposition.split('filename=')[1].replace(/['"]/g, '').split('.').pop() || 'pdf';
                filename = `${type}_${nom}_${cognoms}.${originalExt}`.replace(/\s+/g, '_');
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            message.error('Error descarregant document');
            console.error(e);
        }
    };

    const columns = [
        {
            title: 'Alumne',
            key: 'alumne',
            width: 250,
            render: (_: any, r: any) => (
                <Space>
                    <Avatar src={r.avatar} icon={<UserOutlined />} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <b style={{ fontSize: '15px' }}>{r.nom} {r.cognoms}</b>
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}><MailOutlined /> {r.email || 'Sense email'}</span>
                    </div>
                </Space>
            )
        },
        {
            title: 'Població / CP',
            key: 'poblacio',
            render: (_: any, r: any) => (
                <div style={{ fontSize: '13px' }}>
                    <div><HomeOutlined /> {r.municipi || '---'}</div>
                    <div style={{ color: '#8c8c8c', marginLeft: 18 }}>{r.codi_postal || ''}</div>
                </div>
            )
        },
        {
            title: 'Curs / Grup',
            key: 'curs',
            render: (_: any, r: any) => (
                <Tag color="blue">{r.grup && r.curs ? `${r.grup} / ${r.curs}` : (r.grup_actual || 'No assignat')}</Tag>
            )
        },
        {
            title: 'Estat DUAL',
            key: 'estat',
            render: (_: any, r: any) => {
                // Map estat IDs to labels and colors (same as Asignaciones.tsx)
                const ESTATS: Record<number, { label: string, color: string }> = {
                    0:  { label: 'Entrevista',         color: 'blue' },
                    1:  { label: 'Pendent Acord',      color: 'cyan' },
                    4:  { label: 'Firma Centre',       color: 'purple' },
                    3:  { label: 'Firma Empresa',      color: 'geekblue' },
                    2:  { label: 'Firma Alumne',       color: 'orange' },
                    5:  { label: 'Entrega Resp.',      color: 'magenta' },
                    6:  { label: 'En Pràctiques 🟢',   color: 'success' },
                    7:  { label: 'Finalitzada',        color: 'gold' },
                    8:  { label: 'Pendent Junta',      color: 'volcano' },
                };

                if (r.id_empresa && r.assignacio_estat !== null && r.assignacio_estat !== undefined) {
                    const estat = ESTATS[r.assignacio_estat];
                    return (
                        <div>
                            <Tag color={estat?.color || 'default'} style={{ marginBottom: 2 }}>
                                {estat?.label || `Estat ${r.assignacio_estat}`}
                            </Tag>
                            <div style={{ fontSize: '11px', color: '#555', marginTop: 2 }}>
                                🏢 {r.nom_empresa}
                            </div>
                        </div>
                    );
                }
                return <Tag color="orange">Pendent Assignació</Tag>;
            }
        },
        {
            title: 'Documents (CV / NUSS)',
            key: 'cv',
            width: 150,
            align: 'center' as const,
            render: (_: any, r: any) => {
                const userId = r.id || r.id_usuari;
                return (
                    <Space direction="vertical" size="small">
                        <Space>
                            <span style={{ fontSize: '11px', fontWeight: 'bold', width: 35 }}>CV</span>
                            <Tooltip title="Baixar / Veure CV">
                                <Button 
                                    shape="circle" 
                                    size="small"
                                    type="primary"
                                    icon={<DownloadOutlined />} 
                                    onClick={() => handleDownload(userId, r.nom, r.cognoms, 'CV')}
                                    disabled={(!r.has_cv && r.has_cv !== 1) || !userId} 
                                    style={{ backgroundColor: r.has_cv ? '#52c41a' : undefined, borderColor: r.has_cv ? '#52c41a' : undefined }}
                                />
                            </Tooltip>
                            {/* Upload feature is removed from the list to avoid clutter, they should use DualProfileForm */}
                        </Space>
                        <Space>
                            <span style={{ fontSize: '11px', fontWeight: 'bold', width: 35 }}>NUSS</span>
                            <Tooltip title="Baixar / Veure NUSS">
                                <Button 
                                    shape="circle" 
                                    size="small"
                                    type="primary"
                                    icon={<DownloadOutlined />} 
                                    onClick={() => handleDownload(userId, r.nom, r.cognoms, 'NUSS')}
                                    disabled={(!r.has_nuss && r.has_nuss !== 1) || !userId} 
                                    style={{ backgroundColor: r.has_nuss ? '#52c41a' : undefined, borderColor: r.has_nuss ? '#52c41a' : undefined }}
                                />
                            </Tooltip>
                        </Space>
                    </Space>
                );
            }
        },
        {
            title: 'Accions',
            key: 'action',
            width: 120,
            align: 'right' as const,
            render: (_: any, record: any) => (
                <Space>
                    <Tooltip title="Editar dades DUAL">
                        <Button 
                            type="primary"
                            ghost
                            icon={<EditOutlined />} 
                            onClick={() => { setSelectedAlumneForAssign(record); setIsDetailModalVisible(true); }} 
                        />
                    </Tooltip>
                    <Tooltip title="Assignar a Empresa">
                        <Button 
                            icon={<ShopOutlined />} 
                            onClick={() => handleOpenAssign(record)} 
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 20 }}>
            <div style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <Select style={{ width: 250 }} value={selectedAula} onChange={setSelectedAula}>
                    <Option value="0">Totes les aules</Option>
                    {aulas.map((aula: any) => <Option key={aula.id} value={aula.id.toString()}>{aula.nom}</Option>)}
                </Select>
                <Input 
                    placeholder="Nom, Email..." 
                    value={filters.nom} 
                    onChange={e => setFilters({ ...filters, nom: e.target.value })} 
                    style={{ width: 200 }} 
                    allowClear 
                />
                <Input 
                    placeholder="Població..." 
                    value={filters.poblacio} 
                    onChange={e => setFilters({ ...filters, poblacio: e.target.value })} 
                    style={{ width: 150 }} 
                    allowClear 
                />
                <TreeSelect 
                    treeData={cursosTree} 
                    placeholder="Filtrar per Curs" 
                    style={{ width: 200 }} 
                    allowClear 
                    value={filters.idCurs || undefined} 
                    onChange={(v: any) => setFilters({ ...filters, idCurs: v || 0 })} 
                />
                <Button onClick={() => {
                    setFilters({ nom: '', idCurs: 0, poblacio: '' });
                    setSelectedAula('0');
                }}>Netejar</Button>
            </div>

            <Table 
                columns={columns} 
                dataSource={alumnes} 
                rowKey={(r: any) => r.id || r.id_usuari} 
                loading={loading} 
                pagination={{ pageSize: 10 }}
                bordered
            />

            {/* Modal de Detall */}
            <Modal
                title={`Fitxa Detallada DUAL: ${selectedAlumneForAssign?.nom} ${selectedAlumneForAssign?.cognoms}`}
                open={isDetailModalVisible}
                onCancel={() => setIsDetailModalVisible(false)}
                footer={null}
                width={1000}
                centered
                destroyOnClose
                maskClosable={false}
            >
                {selectedAlumneForAssign && (
                    <DualProfileForm
                        idUsuari={selectedAlumneForAssign.id || selectedAlumneForAssign.id_usuari}
                        onSuccess={() => {
                            setIsDetailModalVisible(false);
                            fetchAlumnes();
                        }}
                    />
                )}
            </Modal>

            {/* Modal d'Assignació d'Empresa */}
            <Modal
                title="Assignar Empresa"
                open={isAssignModalVisible}
                onCancel={() => setIsAssignModalVisible(false)}
                onOk={handleAssignCompany}
                okText="Assignar"
                cancelText="Cancel·lar"
                destroyOnClose
            >
                <div style={{ padding: '10px 0' }}>
                    <p>Selecciona l'empresa per a <b>{selectedAlumneForAssign?.nom} {selectedAlumneForAssign?.cognoms}</b>:</p>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Cerca empresa..."
                        optionFilterProp="children"
                        onChange={(v) => setSelectedCompanyId(v)}
                        value={selectedCompanyId || undefined}
                    >
                        {companiesList.map((c: any) => (
                            <Option key={c.id} value={c.id.toString()}>{c.nom}</Option>
                        ))}
                    </Select>
                </div>
            </Modal>
        </div>
    );
};

export default Alumnos;
